import {
    createContext,
    useContext,
    useMemo,
    type ComponentPropsWithRef,
    type ReactElement,
    type ReactNode,
} from 'react';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '../containers/dialog.tsx';
import { Tooltip, TooltipContent, TooltipTrigger, type TooltipContentProps } from '../primitives/tooltip.tsx';
import { cn } from '../util/classes.ts';
import { useBreakpoint } from '../util/use-breakpoint.tsx';
import { useControllableState } from '../util/use-controllable-state.tsx';

type InfoHintContextValue = {
    delay: number;
    isDesktop: boolean;
    setOpen: (open: boolean) => void;
};

const InfoHintContext = createContext<InfoHintContextValue | undefined>(undefined);

function useInfoHint(part: string): InfoHintContextValue {
    const context = useContext(InfoHintContext);
    if (context === undefined) {
        throw new Error(`${part} must be rendered inside an InfoHint.`);
    }
    return context;
}

export type InfoHintProps = {
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Hover delay before the desktop tooltip opens, in milliseconds. */
    delay?: number;
    children?: ReactNode;
};

/**
 * A `?` glyph that reveals a short explanation: a tooltip on hover from `sm` up, a
 * content-height bottom sheet on tap below it. Holds the open state, renders no DOM.
 *
 * Supplementary by construction: anything with an action in it belongs in a `Dialog`.
 */
export function InfoHint({
    children,
    defaultOpen = false,
    delay = 300,
    onOpenChange,
    open,
}: InfoHintProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const [isOpen, setOpen] = useControllableState({
        value: open,
        defaultValue: defaultOpen,
        onChange: onOpenChange,
    });
    const context = useMemo<InfoHintContextValue>(
        () => ({ delay, isDesktop, setOpen }),
        [delay, isDesktop, setOpen],
    );

    return (
        <InfoHintContext.Provider value={context}>
            {isDesktop ? (
                <Tooltip open={isOpen} onOpenChange={setOpen}>
                    {children}
                </Tooltip>
            ) : (
                <Dialog open={isOpen} onOpenChange={setOpen}>
                    {children}
                </Dialog>
            )}
        </InfoHintContext.Provider>
    );
}

export type InfoHintTriggerProps = ComponentPropsWithRef<'button'>;

/**
 * Opens the hint: hover or click above `sm`, tap below it. Given nothing it presses as a
 * bare `InfoHintIcon`, which is what most hints want; given children they become the
 * press target — an `InfoHintLabel` with the glyph beside it.
 *
 * Keep the label short: a button is an atomic inline box, so a long one drops to the
 * next line whole rather than breaking.
 */
export function InfoHintTrigger({
    children,
    className,
    onClick,
    ...props
}: InfoHintTriggerProps): ReactElement {
    const { delay, isDesktop, setOpen } = useInfoHint('InfoHintTrigger');

    const button = (
        <button
            className={cn(
                'group inline-flex cursor-help items-center gap-tight align-middle text-left',
                // Pads out to the interactive minimum the glyph misses, then gives the
                // vertical half back so the line box stays put.
                'p-1.5 -my-1.5',
                className,
            )}
            onClick={(event) => {
                onClick?.(event);
                setOpen(true);
            }}
            type="button"
            {...props}
        >
            {children ?? <InfoHintIcon />}
        </button>
    );

    // Below `sm` the button drives the sheet itself: `DialogTrigger` can only ever
    // render a `Button`, which the glyph is not.
    return isDesktop ? <TooltipTrigger delay={delay} render={button} /> : button;
}

export type InfoHintIconProps = Omit<ComponentPropsWithRef<'span'>, 'children'> & {
    /** What a screen reader reads for the glyph, which is otherwise silent. */
    label?: string;
};

/**
 * The `?` glyph: neutral at rest, brand-tinted on hover, and carrying the name its
 * trigger reads by. `?` over `i` because an `i` is also how the UI marks status. Too
 * small to press on its own — the trigger around it carries the target.
 */
export function InfoHintIcon({ className, label = 'Info', ...props }: InfoHintIconProps): ReactElement {
    return (
        <span
            className={cn(
                'relative inline-flex size-[18px] items-center justify-center rounded-full align-middle',
                'bg-content/(--opacity-wash) text-ui-sm leading-none font-medium text-content-mid',
                'transition duration-state ease-state',
                'group-hover:bg-brand/(--opacity-wash) group-hover:text-brand',
                className,
            )}
            {...props}
        >
            <span aria-hidden>?</span>
            <span className="sr-only">{label}</span>
        </span>
    );
}

export type InfoHintLabelProps = ComponentPropsWithRef<'span'>;

/**
 * The value a trigger presses by, dashed-underlined to say there is more to read. The
 * underline lives here because on the trigger it would be drawn across the glyph too,
 * and no descendant can cancel it.
 */
export function InfoHintLabel({ className, ...props }: InfoHintLabelProps): ReactElement {
    return (
        <span
            className={cn(
                'underline decoration-neutral-300 decoration-dashed decoration-[1.5px] underline-offset-2',
                className,
            )}
            data-slot="info-hint-label"
            {...props}
        />
    );
}

export type InfoHintHeaderProps = ComponentPropsWithRef<'div'>;

/**
 * The band the title sits in. Below `sm` it is the sheet's header and brings the
 * close control with it, a hint's header being only ever a title and a way out.
 */
export function InfoHintHeader({ children, className, ...props }: InfoHintHeaderProps): ReactElement {
    const { isDesktop } = useInfoHint('InfoHintHeader');

    if (isDesktop) {
        // `contents` so the popup's own column lays the title out, leaving no gap
        // behind an empty header.
        return (
            <div className={cn('contents', className)} {...props}>
                {children}
            </div>
        );
    }

    return (
        // Tighter below than a dialog: title and explanation are one thought.
        <DialogHeader className={cn('pb-cozy', className)} {...props}>
            {children}
            <DialogIconClose />
        </DialogHeader>
    );
}

export type InfoHintTitleProps = ComponentPropsWithRef<'div'>;

/**
 * Names the thing being explained, as a `DialogTitle` names a dialog. Render it empty
 * where there is no label: the sheet reads "Info" and the popup shows no heading.
 */
export function InfoHintTitle({ children, className, ...props }: InfoHintTitleProps): ReactElement | null {
    const { isDesktop } = useInfoHint('InfoHintTitle');

    if (!isDesktop) {
        return (
            <DialogTitle className={cn('text-card-title', className)} {...props}>
                {children ?? 'Info'}
            </DialogTitle>
        );
    }

    if (children === undefined) {
        return null;
    }

    return (
        <div className={cn('font-semibold', className)} data-slot="info-hint-title" {...props}>
            {children}
        </div>
    );
}

export type InfoHintBodyProps = ComponentPropsWithRef<'div'>;

/** The explanation itself: the sheet's scrolling body below `sm`, plain text above. */
export function InfoHintBody({ className, ...props }: InfoHintBodyProps): ReactElement {
    const { isDesktop } = useInfoHint('InfoHintBody');

    if (isDesktop) {
        return <div className={className} data-slot="info-hint-body" {...props} />;
    }

    return <DialogBody className={cn('text-body text-content-weaker', className)} {...props} />;
}

export type InfoHintContentProps = Pick<
    TooltipContentProps,
    'align' | 'alignOffset' | 'side' | 'sideOffset'
> & {
    children?: ReactNode;
    className?: string;
};

/**
 * The surface the explanation arrives on: a tooltip popup above `sm`, a content-height
 * sheet below it. The placement props aim the popup and the sheet ignores them.
 */
export function InfoHintContent({
    align,
    alignOffset,
    children,
    className,
    side,
    sideOffset,
}: InfoHintContentProps): ReactElement {
    const { isDesktop } = useInfoHint('InfoHintContent');

    if (isDesktop) {
        return (
            <TooltipContent
                align={align}
                alignOffset={alignOffset}
                className={cn('flex-col items-start gap-tight', className)}
                side={side}
                sideOffset={sideOffset}
            >
                {children}
            </TooltipContent>
        );
    }

    return (
        <DialogContent className={className} snapMode="contain">
            {children}
        </DialogContent>
    );
}
